import React from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  TextField,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PersonalInfoForm from "../PersonalInfoForm/PersonalInfoForm";

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
  },
};

/**
 * Personal Information section + Ownership shares (must total 100%).
 * Uses shared PersonalInfoForm: once for primary owner, then same form for each co-owner with Share % added.
 */
const PersonalInfoOwnershipForm = ({
  value = {},
  errors = {},
  onChange,
  coOwners = [],
  coOwnerErrors = {},
  onUpdateCoOwner,
  onAddCoOwner,
  onRemoveCoOwner,
  totalOwnershipPercent = 0,
  ownershipError = "",
}) => {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          mb: 3,
          color: "#495057",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 2,
          borderBottom: "2px solid #e9ecef",
        }}
      >
        👤 Personal Information
      </Typography>

      {/* Primary owner: same form (no Share %) */}
      <PersonalInfoForm
        value={value}
        errors={errors}
        onChange={onChange}
        showSharePercentage={false}
      />

      {/* Ownership shares – primary % + total; co-owners use PersonalInfoForm with Share % */}
      <Box sx={{ mt: 4, mb: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "#495057",
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          📊 Ownership shares (must total 100%)
        </Typography>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item size={{ xs: 12, md: 3, lg: 3 }}>
            <TextField
              variant="outlined"
              label="Primary owner share %"
              name="primaryOwnerPercentage"
              value={value.primaryOwnerPercentage ?? ""}
              onChange={onChange}
              fullWidth
              type="number"
              inputProps={{ min: 0, max: 100, step: 0.5 }}
              placeholder="e.g. 60"
              helperText={ownershipError ? "" : "Your share as primary"}
              error={!!ownershipError}
              sx={textFieldSx}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md={3}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Typography variant="body2" color="text.secondary">
              Total: <strong>{totalOwnershipPercent.toFixed(1)}%</strong>
              {Math.abs(totalOwnershipPercent - 100) > 0.01 && (
                <span style={{ color: "#d32f2f", marginLeft: 8 }}>
                  — must be 100%
                </span>
              )}
            </Typography>
          </Grid>
        </Grid>
        {ownershipError && (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 0.5 }}
          >
            {ownershipError}
          </Typography>
        )}

        {/* Co-owners: same Personal Information form + Share % */}
        {coOwners.map((co) => (
          <Paper
            key={co.id}
            variant="outlined"
            sx={{
              p: 2,
              mt: 2,
              borderRadius: 2,
              borderColor: "#e0e0e0",
            }}
          >
            <PersonalInfoForm
              value={co}
              errors={coOwnerErrors[co.id] || {}}
              onChange={(e) =>
                onUpdateCoOwner(co.id, e.target.name, e.target.value)
              }
              showSharePercentage={true}
              sharePercentError={
                totalOwnershipPercent > 0 &&
                Math.abs(totalOwnershipPercent - 100) > 0.01
                  ? "Total must be 100%"
                  : ""
              }
              onRemove={() => onRemoveCoOwner(co.id)}
              compact={true}
            />
          </Paper>
        ))}
        <Button
          startIcon={<AddIcon />}
          onClick={onAddCoOwner}
          variant="outlined"
          size="medium"
          sx={{
            mt: 2,
            borderRadius: 2,
            borderColor: "#465064",
            color: "#465064",
          }}
        >
          Add co-owner
        </Button>
      </Box>
    </Box>
  );
};

export default PersonalInfoOwnershipForm;
